/**
 * ModalEmbalagemNovoProduto — a EMBALAGEM do item da prateleira NOVOS PRODUTOS, editada de dentro da
 * cotacao (pedido do Ruan, 31/10/2026, print do NP00035 em "UN" sem Qtd: "nao consigo alterar esta
 * informacao"). Irma do `ModalEmbalagemCompra` (o do produto, [DA-856]): mesmo clique em Embal./Qtd
 * Emb, mesma lista de unidades com o `+`.
 *
 * Grava pelo `PATCH /api/novos-produtos/:id/embalagem`, que escreve SO estes dois campos. Sem caneta:
 * o cadastro do Novo Produto nao tem trava de edicao em lugar nenhum (o PUT da tela dele tambem nao).
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { formatQtd, parseQtd } from '@fiodecobre/shared';
import { api, ApiError } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { ComboBox } from '../../components/ComboBox';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';

interface OpUnidade { id: number; sigla: string; nome: string; ativo?: boolean }
function unidadesOfertadas(lista: OpUnidade[], atual: string) {
  return lista.filter((u) => u.ativo !== false || u.sigla === atual);
}
const rotuloUnidade = (u: OpUnidade) => `${u.sigla} — ${u.nome}${u.ativo === false ? ' (inativa)' : ''}`;

export interface EmbalagemNovoProdutoGravada {
  embalagem: string | null;
  quantidade_embalagem: string | null;
}

export function ModalEmbalagemNovoProduto(props: {
  novoProdutoId: number;
  codigo: string | null;
  descricao: string | null;
  embalagemAtual: string | null;
  qtdAtual: string | number | null;
  onFechar: () => void;
  onGravado: (g: EmbalagemNovoProdutoGravada) => void;
}) {
  const { novoProdutoId, codigo, descricao, embalagemAtual, qtdAtual, onFechar, onGravado } = props;

  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [unidades, setUnidades] = useState<OpUnidade[]>([]);
  const qtdInicial = qtdAtual != null && Number(qtdAtual) > 0 ? formatQtd(Number(qtdAtual)) : '';
  const [embalagem, setEmbalagem] = useState((embalagemAtual ?? '').toUpperCase());
  const [qtd, setQtd] = useState(qtdInicial);
  const original = useRef(JSON.stringify([(embalagemAtual ?? '').toUpperCase(), qtdInicial]));
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [novaUnidade, setNovaUnidade] = useState(false);
  const qtdRef = useRef<HTMLInputElement>(null);
  const salvarRef = useRef<HTMLButtonElement>(null);

  const alterado = JSON.stringify([embalagem, qtd]) !== original.current;

  useEffect(() => {
    let vivo = true;
    api.get<OpUnidade[]>('/api/unidades-medida')
      .then((u) => { if (vivo) setUnidades(Array.isArray(u) ? u : []); })
      .catch((e: any) => { if (vivo) setErroCarga(e?.message || 'Nao consegui ler as unidades.'); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, []);

  const qtdNum = qtd.trim() ? parseQtd(qtd) : null;
  const motivoSemSalvar = !alterado
    ? 'Nada foi alterado.'
    : (qtdNum !== null && !(Number.isFinite(qtdNum) && qtdNum > 0) ? 'Quantidade invalida.' : null);

  async function salvar() {
    if (motivoSemSalvar) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const r = await api.patch<EmbalagemNovoProdutoGravada>(`/api/novos-produtos/${novoProdutoId}/embalagem`, {
        embalagem: embalagem || null,
        quantidade_embalagem: qtdNum,
      });
      onGravado({ embalagem: r.embalagem ?? null, quantidade_embalagem: r.quantidade_embalagem ?? null });
    } catch (e: any) {
      setErroSalvar(e instanceof ApiError ? e.message : (e?.message || 'Nao consegui gravar. Nada foi salvo.'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Modal
        aberto
        titulo="Embalagem do Novo Produto"
        onFechar={onFechar}
        largura={460}
        compacto
        rodape={
          <div className="flex items-center justify-end gap-2 w-full">
            {erroSalvar && <span className="text-sm mr-auto" style={{ color: 'var(--danger, #b91c1c)', fontWeight: 600 }}>{erroSalvar}</span>}
            {!erroSalvar && motivoSemSalvar && <span className="text-sm mr-auto" style={{ color: 'var(--text-muted)' }}>{motivoSemSalvar}</span>}
            <button type="button" className="btn-secundario" onClick={onFechar}>
              {alterado ? 'Cancelar' : 'Fechar'}
            </button>
            <button
              ref={salvarRef}
              type="button"
              className="btn-primario inline-flex items-center gap-1"
              onClick={() => void salvar()}
              disabled={!!motivoSemSalvar || carregando || salvando || !!erroCarga}
            >
              {salvando && <Loader2 size={14} className="animate-spin" />}
              Salvar
            </button>
          </div>
        }
      >
        <div className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
          <span style={{ fontWeight: 700, color: 'var(--text)' }}>{codigo || '—'}</span>
          {' · '}
          {descricao || ''}
        </div>

        {carregando ? (
          <div className="flex items-center gap-2 text-sm py-4" style={{ color: 'var(--text-muted)' }}>
            <Loader2 size={16} className="animate-spin" /> Carregando…
          </div>
        ) : erroCarga ? (
          <div className="text-sm py-2" style={{ color: 'var(--danger, #b91c1c)', fontWeight: 600 }}>{erroCarga}</div>
        ) : (
          <fieldset disabled={salvando} style={{ border: 0, padding: 0, margin: 0 }}>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
              Embalagem <span className="font-normal" style={{ color: 'var(--text-muted)' }}>(+ Qtd na Emb)</span>
            </label>
            <div className="flex gap-1">
              <div className="flex-1 min-w-0">
                <ComboBox
                  opcoes={unidadesOfertadas(unidades, embalagem).map((u) => ({ value: u.sigla, label: rotuloUnidade(u) }))}
                  value={embalagem}
                  onChange={(v) => setEmbalagem(String(v))}
                  placeholder="Selecione..."
                  autoFocus
                  aoAvancar={() => qtdRef.current?.focus()}
                />
              </div>
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setNovaUnidade(true)}
                className="px-2 py-1 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-500 rounded-lg transition flex-shrink-0"
                title="Nova unidade"
              >
                <Plus size={16} />
              </button>
              <input
                ref={qtdRef}
                type="text"
                inputMode="decimal"
                name="quantidade_embalagem"
                value={qtd}
                onChange={(e) => setQtd(e.target.value)}
                onBlur={(e) => {
                  const n = parseQtd(e.target.value);
                  setQtd(Number.isFinite(n) && n > 0 ? formatQtd(n) : '');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && salvarRef.current && !salvarRef.current.disabled) { e.preventDefault(); salvarRef.current.focus(); }
                }}
                placeholder="Qtd"
                className="cel-num w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-sm flex-shrink-0"
                title="Quantas unidades vem na embalagem (ex: 10 no pacote)."
              />
            </div>
          </fieldset>
        )}
      </Modal>

      {novaUnidade && (
        <ModalCadastroRapido
          titulo="Nova Unidade"
          endpoint="/api/unidades-medida"
          campos={[
            { key: 'sigla', label: 'Sigla', obrigatorio: true },
            { key: 'nome', label: 'Nome', obrigatorio: true },
            { key: 'permite_fracionado', label: 'Permite fracionado', tipo: 'boolean', descricao: 'Permite venda fracionada' },
          ]}
          onSalvo={(novo: OpUnidade) => {
            setUnidades((p) => [...p, novo]);
            setEmbalagem(novo.sigla);
            setNovaUnidade(false);
          }}
          onFechar={() => setNovaUnidade(false)}
        />
      )}
    </>
  );
}
